import { SERVER, api, setMsg } from "./api.js";
import "../components/shell.js";

const APPROVER_ROLES = new Set(["professor", "admin"]);

// Returns null when the element isn't on this page - callers must guard
const $ = (id) => document.getElementById(id);

function pageForRole(role) {
   if (role === "admin") return "admin.html";
   if (role === "professor") return "professor.html";
   return "student.html";
}

/* ================= LOGIN PAGE (web.html) ================= */

// Surface ?login=unauthorized|failed|error from the OAuth callback redirect.
function readLoginError() {
   const reason = new URLSearchParams(location.search).get("login");
   if (!reason) return;
   const text = {
      unauthorized: "That account isn't approved yet. Ask your professor for access.",
      failed: "Sign-in failed. Try again",
      error: "Something went wrong signing in. Try again.",
   };
   setMsg("login-error", text[reason] || "Sign-in failed.", "err");
   history.replaceState({}, "", location.pathname);
}

async function initLogin() {
   readLoginError();
   $("btn-login-ms")?.addEventListener("click", () => {
      location.href = `${SERVER}/auth/microsoft/web`;
   });
   $("btn-login")?.addEventListener("click", () => {
      location.href = `${SERVER}/auth/google/web`;
   });

   // Already signed in (e.g. arriving back from the OAuth callback)? Go to the app.
   try {
      const res = await api("/auth/status");
      const data = await res.json();
      if (data.authenticated) {
         const meRes = await api("/me");
         const me = await meRes.json();
         if (me.ok) location.replace(pageForRole(me.role));
      }
   } catch {
      // stay on login
   }
}

/* ================= APP PAGES (student/professor/admin.html) ================= */

async function initApp() {
   let me;
   try {
      const res = await api("/me");
      if (res.status === 401) return location.replace("web.html");
      me = await res.json();
      if (!me.ok) throw new Error("bad /me");
   } catch {
      return location.replace("web.html");
   }

   // Header (rendered by <app-header>)
   const sub = $("subtitle");
   if (sub) sub.textContent = `${me.email} · ${me.role}`;
   $("btn-logout")?.addEventListener("click", async () => {
      await api("/auth/logout", { method: "POST" });
      location.href = "web.html";
   });

   // Approvals (professor / admin only). Server still enforces the real guard.
   if (APPROVER_ROLES.has(me.role) && $("card-approvals")) {
      $("btn-refresh")?.addEventListener("click", loadRequests);
      loadRequests();
   }

   initRouter();
}

async function loadRequests() {
   const list = $("requests");
   if (!list) return;
   list.textContent = "Loading…";
   try {
      const res = await api("/auth/requests");
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.reason || "failed");

      const requests = data.requests;
      list.textContent = "";
      if (requests.length === 0) {
         const p = document.createElement("p");
         p.className = "empty";
         p.textContent = "No pending requests.";
         list.appendChild(p);
         return;
      }
      for (const r of requests) {
         const row = document.createElement("div");
         row.className = "row";
         const left = document.createElement("div");
         const email = document.createElement("div");
         email.className = "email";
         email.textContent = r.email; // textContent, not innerHTML — user input
         const when = document.createElement("div");
         when.className = "when";
         when.textContent = new Date(r.requested_at).toLocaleString();
         left.append(email, when);
         const btn = document.createElement("button");
         btn.textContent = "Approve";
         btn.addEventListener("click", () => approve(r.email, btn));
         row.append(left, btn);
         list.appendChild(row);
      }
   } catch {
      list.textContent = "";
      setMsg("approve-msg", "Couldn't load requests.", "err");
   }
}

async function approve(email, btn) {
   btn.disabled = true;
   btn.textContent = "Approving…";
   setMsg("approve-msg", "");
   try {
      const res = await api("/auth/approve", {
         method: "POST",
         body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.reason || "failed");
      setMsg("approve-msg", `Approved ${email}.`, "ok");
      await loadRequests();
   } catch {
      setMsg("approve-msg", `Couldn't approve ${email}.`, "err");
      btn.disabled = false;
      btn.textContent = "Approve";
   }
}

function initRouter() {
   const views = [...document.querySelectorAll("[data-view]")];
   if (!views.length) return;

   function route() {
      const hash = location.hash.slice(1) || "classes";    // default view
      const [name, ...rest] = hash.split("/");
      const param = rest.length ? decodeURIComponent(rest.join("/")) : undefined;
      let matched = false;

      for (const el of views) {
         const isActive = el.dataset.view === name;
         el.hidden = !isActive;
         if (isActive) {
            matched = true;
            /*
            Find the view component by CAPABILITY, not by position.

            ⚠️ This used to be `el.firstElementChild`, and that broke silently the
            day help panels shipped. `addHelpPanels()` in shell.js PREPENDS an
            <app-help> into every [data-view] block, so the first element child
            became the help banner, which has no show(). The result: show() was
            never called for any view that had a help entry, so `#training/<slug>`
            changed the address bar and rendered nothing, and every view with a
            panel silently lost its refresh-on-navigate.

            Positional coupling between two files that do not know about each
            other is the bug. Asking which child can actually be shown cannot be
            broken by prepending anything.
            */
            const comp = [...el.children].find((c) => typeof c.show === "function");
            if (comp) comp.show(param);
         }
      }

      if (!matched) {
         location.replace("#classes");
      }
   }

   window.addEventListener("hashchange", route);
   route();
}

/* ================= BOOT ================= */
if ($("btn-login")) {
   initLogin();                                   // web.html
} else if (document.querySelector("app-shell")) {
   initApp();                                     // student/professor/admin.html
}